import { useCallback } from "react";
import { useMatch } from "react-router-dom";
import { EVENT_WORKSPACE_TABS } from "./navigation";

/*
  Links between the pages of an event module.

  Module screens are shown at their own address and inside an event workspace (/events/:eventId). A link that
  names the module address would leave the workspace and drop its tabs, although the event stays selected. Links
  from module screens therefore go through useWorkspacePath: inside a workspace it names the same page there, and
  anywhere else it leaves the address as it is.
*/

/** Module addresses whose workspace page has another name. */
const ALIASES: [from: string, to: string][] = [
  ["/sponsors/marketplace", "/exhibitors"],
  ["/procurement", "/vendors"],
];

/** The module sections a workspace holds. Settings is the workspace's own page and has no module address. */
const SECTIONS = new Set(
  EVENT_WORKSPACE_TABS.map((tab) => tab.path).filter((path) => path !== "" && path !== "settings"),
);

/** A record's id in an address. */
const RECORD_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A module address as the same page in the workspace of `eventId`. Addresses no workspace holds are returned as given. */
export function workspacePath(eventId: string, to: string): string {
  const split = to.search(/[?#]/);
  const pathname = split < 0 ? to : to.slice(0, split);
  const rest = split < 0 ? "" : to.slice(split);

  const alias = ALIASES.find(([from]) => pathname === from || pathname.startsWith(`${from}/`));
  const path = alias ? `${alias[1]}${pathname.slice(alias[0].length)}` : pathname;

  return SECTIONS.has(path.split("/")[1] ?? "") ? `/events/${eventId}${path}${rest}` : to;
}

/** Turns a module address into its workspace page while rendered inside a workspace; elsewhere it is left as it is. */
export function useWorkspacePath(): (to: string) => string {
  const eventId = useMatch("/events/:eventId/*")?.params.eventId;

  return useCallback((to: string) => (eventId ? workspacePath(eventId, to) : to), [eventId]);
}

/**
 * The page of a workspace to open when another event is chosen there. A page showing one record belongs to the
 * event being left, so the other event opens on that module's own page instead.
 */
export function sectionForAnotherEvent(section: string): string {
  const segments = section.split("/");

  return segments.some((segment) => RECORD_ID.test(segment)) ? `/${segments[1]}` : section;
}
